import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessagesModule } from 'primeng/messages';

import { BlogNewsService } from 'src/app/services/blog-news.service';
import { BlogPost } from 'src/app/models/blog-post';

/**
 * Blogs and news page component.
 */
@Component({
	selector: 'app-blogs-news',
	standalone: true,
	imports: [
		CommonModule,
		CardModule,
		ProgressSpinnerModule,
		MessagesModule,
	],
	providers: [BlogNewsService],
	templateUrl: './blogs-news.component.html',
	styleUrl: './blogs-news.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogsNewsComponent {
	private blogNewsService = inject(BlogNewsService);

	/**
	 * Title of the page.
	 */
	public title = 'Blogs & News';

	/**
	 * Blog posts observable.
	 */
	public blogPosts$ = this.blogNewsService.blogPosts$;

	/**
	 * State management.
	 */
	public isLoading = signal(false);
	public error = signal('');
	public expandedPosts = signal<Set<number>>(new Set());

	/**
	 * Truncates text to a specified length.
	 */
	public truncateText(text: string, length: number = 300): string {
		if (!text) return '';
		return text.length > length ? text.substring(0, length) + '...' : text;
	}

	/**
	 * Extracts title from post content (assumes first line or specific pattern).
	 */
	public extractTitle(post: string): string {
		if (!post) return 'Blog Post';
		
		// Try to extract title from first line or use a pattern
		const lines = post.split('\n');
		const firstLine = lines[0]?.trim();
		
		// If first line looks like a title (short and doesn't end with punctuation)
		if (firstLine && firstLine.length < 100 && !firstLine.endsWith('.')) {
			return firstLine;
		}
		
		// Otherwise use first 50 characters as title
		return this.truncateText(post, 50);
	}

	/**
	 * Extracts content from post (everything after title).
	 */
	public extractContent(post: string): string {
		if (!post) return '';
		
		const lines = post.split('\n');
		if (lines.length > 1) {
			return lines.slice(1).join('\n').trim();
		}
		
		return post;
	}

	/**
	 * Toggles the expanded state of a post.
	 */
	public toggleExpanded(index: number): void {
		const currentExpanded = this.expandedPosts();
		const newExpanded = new Set(currentExpanded);
		
		if (newExpanded.has(index)) {
			newExpanded.delete(index);
		} else {
			newExpanded.add(index);
		}
		
		this.expandedPosts.set(newExpanded);
	}

	/**
	 * Checks if a post is expanded.
	 */
	public isExpanded(index: number): boolean {
		return this.expandedPosts().has(index);
	}

	/**
	 * Converts basic markdown to HTML for display.
	 */
	public markdownToHtml(text: string): string {
		if (!text) return '';

		let html = text
			// Headers
			.replace(/^### (.*$)/gim, '<h3>$1</h3>')
			.replace(/^## (.*$)/gim, '<h2>$1</h2>')
			.replace(/^# (.*$)/gim, '<h1>$1</h1>')
			// Bold
			.replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
			// Italic
			.replace(/\*(.*)\*/gim, '<em>$1</em>')
			// Line breaks
			.replace(/\n/gim, '<br>');

		return html;
	}

	/**
	 * Formats Unix timestamp to readable date string.
	 */
	public formatTimestamp(timestamp?: string): string {
		if (!timestamp) return '';
		
		try {
			// Convert Unix timestamp (string) to number and then to Date
			const date = new Date(parseInt(timestamp) * 1000);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		} catch (error) {
			return '';
		}
	}

	/**
	 * Gets the remaining content after the truncation point.
	 */
	public getRemainingContent(text: string, truncateLength: number = 200): string {
		if (!text || text.length <= truncateLength) return '';
		return text.substring(truncateLength);
	}
} 